import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { http } from "@/lib/api";
import { PLATFORM_MARK } from "@/components/PlatformLogo";
import { ShieldCheck, FileText, Mail, Cookie, ArrowLeft, Trash2 } from "lucide-react";

const FALLBACK = {
  platform_name: "NEXTMIND INFISSI",
  platform_legal_business_name: "NEXTMIND SOFTWARE DI DE BELLIS DANIELE",
  platform_vat_number: "14827340960",
  platform_address: "Via Trento e Trieste 24",
  platform_postal_code: "20822",
  platform_city: "Seveso",
  platform_province: "MB",
  platform_country: "Italia",
  platform_pec: "debellisdaniele91@pec.it",
  platform_rea: "MB-2809698",
};

const UPDATED = "10 giugno 2026";

function usePlatform() {
  const [p, setP] = useState(FALLBACK);
  useEffect(() => {
    http.get("/public/platform-info").then((r) => setP({ ...FALLBACK, ...r.data })).catch(() => {});
  }, []);
  return p;
}

function LegalShell({ icon: Icon, title, children }) {
  const p = usePlatform();
  const addr = `${p.platform_address}, ${p.platform_postal_code} ${p.platform_city} (${p.platform_province}), ${p.platform_country}`;
  return (
    <div className="min-h-screen bg-slate-50 text-slate-800">
      <header className="bg-[#0F172A] text-white">
        <div className="max-w-3xl mx-auto px-5 py-5 flex items-center justify-between">
          <div className="flex items-center gap-2.5" data-testid="legal-platform-logo">
            <img src={PLATFORM_MARK} alt="NEXTMIND INFISSI" className="w-9 h-9 rounded-lg" />
            <div><div className="font-bold leading-tight">{p.platform_name}</div><div className="text-xs text-slate-400">{p.platform_legal_business_name}</div></div>
          </div>
          <Link to="/welcome" className="text-xs text-slate-300 hover:text-white flex items-center gap-1"><ArrowLeft className="w-3.5 h-3.5" /> Home</Link>
        </div>
      </header>
      <main className="max-w-3xl mx-auto px-5 py-10">
        <h1 className="text-3xl sm:text-4xl font-bold text-[#0F172A]">{title}</h1>
        <p className="text-sm text-slate-500 mt-1">Ultimo aggiornamento: {UPDATED}</p>
        <div className="prose prose-slate mt-6 max-w-none text-[15px] leading-relaxed space-y-4">{children}</div>
        <div className="mt-10 border-t border-slate-200 pt-5 text-sm text-slate-600">
          <div className="font-semibold text-[#0F172A]">Titolare del trattamento / Fornitore del servizio</div>
          <p className="mt-1">
            {p.platform_legal_business_name}<br />
            {addr}<br />
            P.IVA: {p.platform_vat_number} · REA: {p.platform_rea}<br />
            PEC: <a className="text-sky-700" href={`mailto:${p.platform_pec}`}>{p.platform_pec}</a>
          </p>
        </div>
        <nav className="mt-8 flex flex-wrap gap-4 text-sm text-sky-700">
          <Link to="/privacy">Privacy</Link><Link to="/terms">Termini</Link>
          <Link to="/cookies">Cookie</Link><Link to="/contact">Contatti</Link>
        </nav>
      </main>
    </div>
  );
}

const H = ({ children }) => <h2 className="text-lg font-semibold text-[#0F172A] mt-6">{children}</h2>;

export function DataDeletion() {
  const p = usePlatform();
  return (
    <LegalShell icon={Trash2} title="Richiesta di eliminazione dei dati">
      <p>{p.platform_name} rispetta la privacy degli utenti e consente di richiedere l'eliminazione dei dati personali associati al proprio account e ai servizi collegati.</p>

      <H>Come richiedere l'eliminazione dei dati</H>
      <ol className="list-decimal ml-5 space-y-2">
        <li>Invia una richiesta a: <a className="text-sky-700 font-medium" href="mailto:nextmindsoftware@gmail.com">nextmindsoftware@gmail.com</a></li>
        <li>Nell'oggetto scrivi: <b>"Richiesta eliminazione dati NEXTMIND INFISSI"</b></li>
        <li>Indica:
          <ul className="list-disc ml-5 mt-1 space-y-1">
            <li>nome e cognome</li>
            <li>indirizzo email associato all'account</li>
            <li>nome dell'azienda/tenant</li>
            <li>eventuale numero WhatsApp Business collegato</li>
            <li>richiesta esplicita di eliminazione dei dati</li>
          </ul>
        </li>
      </ol>

      <p>Dopo la verifica dell'identità e della titolarità dell'account, {p.platform_name} provvederà alla cancellazione o anonimizzazione dei dati personali non soggetti a obblighi legali di conservazione.</p>
      <p>La richiesta sarà gestita nel minor tempo possibile e comunque secondo i termini previsti dalla normativa applicabile.</p>
      <p>Alcuni dati potranno essere conservati esclusivamente quando richiesto da obblighi fiscali, contabili, amministrativi, antifrode o di legge.</p>

      <H>Dati collegati a servizi Meta / WhatsApp</H>
      <p>Se l'utente richiede la cancellazione dei dati collegati tramite Meta o WhatsApp Business, {p.platform_name} eliminerà o anonimizzerà i dati applicativi associati al tenant/account, nei limiti tecnici e normativi applicabili.</p>

      <H>Contatti</H>
      <p>Email: <a className="text-sky-700" href="mailto:nextmindsoftware@gmail.com">nextmindsoftware@gmail.com</a><br />
      PEC: <a className="text-sky-700" href={`mailto:${p.platform_pec}`}>{p.platform_pec}</a></p>
    </LegalShell>
  );
}

export function Privacy() {
  const p = usePlatform();
  return (
    <LegalShell icon={ShieldCheck} title="Informativa sulla Privacy">
      <p>La presente informativa descrive come {p.platform_legal_business_name} ("Titolare") tratta i dati personali degli utenti e dei loro clienti nell'ambito del servizio {p.platform_name}, una piattaforma gestionale per aziende di infissi che integra la messaggistica WhatsApp Business tramite la WhatsApp Business Platform di Meta.</p>

      <H>1. Dati raccolti</H>
      <ul className="list-disc ml-5 space-y-1">
        <li><b>Dati dell'account:</b> nome, cognome, email, ruolo, password (cifrata), preferenze di lingua.</li>
        <li><b>Dati aziendali:</b> ragione sociale, P.IVA, indirizzo, logo, dati di contatto e configurazioni.</li>
        <li><b>Dati dei clienti finali:</b> nominativo, numero di telefono WhatsApp, messaggi, foto, note e preventivi inseriti dall'azienda utente.</li>
        <li><b>Messaggi WhatsApp:</b> contenuto delle conversazioni scambiate tramite il numero WhatsApp Business collegato dall'azienda.</li>
        <li><b>Dati tecnici:</b> log di accesso, indirizzo IP, identificativi di sessione e dati necessari alla sicurezza.</li>
      </ul>

      <H>2. Finalità e base giuridica</H>
      <p>I dati sono trattati per: erogare il servizio (esecuzione del contratto), gestire clienti, pratiche e preventivi, inviare e ricevere messaggi WhatsApp per conto dell'azienda utente, garantire sicurezza e prevenire abusi (legittimo interesse) e adempiere a obblighi di legge.</p>

      <H>3. Integrazione WhatsApp / Meta</H>
      <p>Il collegamento del numero avviene tramite Meta Embedded Signup ufficiale. I messaggi transitano attraverso la WhatsApp Business Platform di Meta Platforms Ireland Ltd. secondo le condizioni e l'informativa di Meta. {p.platform_name} tratta tali messaggi esclusivamente per conto dell'azienda utente, che resta titolare dei dati dei propri clienti.</p>

      <H>4. Fornitori terzi (responsabili del trattamento)</H>
      <ul className="list-disc ml-5 space-y-1">
        <li><b>Meta Platforms</b> — messaggistica WhatsApp Business.</li>
        <li><b>OpenAI</b> — elaborazione AI dei messaggi (quando attivata dal titolare della piattaforma).</li>
        <li><b>Stripe</b> — gestione dei pagamenti e degli abbonamenti.</li>
        <li><b>Provider di hosting e invio email</b> — erogazione tecnica del servizio.</li>
      </ul>

      <H>5. Conservazione</H>
      <p>I dati sono conservati per il tempo necessario all'erogazione del servizio e agli obblighi di legge. Alla cessazione del rapporto i dati vengono cancellati o anonimizzati, salvo obblighi di conservazione.</p>

      <H>6. Diritti dell'interessato</H>
      <p>Ai sensi del GDPR (Reg. UE 2016/679) l'interessato può esercitare i diritti di accesso, rettifica, cancellazione, limitazione, portabilità e opposizione, oltre a proporre reclamo all'Autorità Garante. Le richieste vanno inviate alla PEC indicata in calce.</p>

      <H>7. Sicurezza</H>
      <p>Adottiamo misure tecniche e organizzative adeguate: cifratura delle credenziali e dei segreti, controllo degli accessi basato sui ruoli e isolamento dei dati tra le diverse aziende (multi-tenant).</p>

      <H>8. Modifiche</H>
      <p>La presente informativa può essere aggiornata. Le modifiche saranno pubblicate su questa pagina con la relativa data di aggiornamento.</p>
    </LegalShell>
  );
}

export function Terms() {
  const p = usePlatform();
  return (
    <LegalShell icon={FileText} title="Termini di Servizio">
      <p>I presenti Termini regolano l'uso della piattaforma {p.platform_name} fornita da {p.platform_legal_business_name}. Utilizzando il servizio l'utente accetta i presenti Termini.</p>
      <H>1. Servizio</H>
      <p>{p.platform_name} è un software gestionale in abbonamento (SaaS) per aziende di infissi, con gestione di clienti, pratiche, preventivi e messaggistica WhatsApp Business.</p>
      <H>2. Account e responsabilità</H>
      <p>L'utente è responsabile della veridicità dei dati forniti, della custodia delle credenziali e dell'uso conforme alla legge, incluse le normative applicabili a WhatsApp/Meta e alla protezione dei dati dei propri clienti.</p>
      <H>3. Uso consentito</H>
      <p>È vietato l'uso del servizio per invii non richiesti (spam), contenuti illeciti o attività che violino le policy di Meta/WhatsApp. Il mancato rispetto può comportare la sospensione dell'account.</p>
      <H>4. Abbonamento e pagamenti</H>
      <p>Il servizio prevede 7 giorni di prova gratuita senza obbligo; al termine è necessario attivare un abbonamento (€60/mese oppure €600/anno) tramite carta di credito/debito con Stripe. Il mancato pagamento può comportare la limitazione delle funzionalità; i dati restano conservati e disponibili alla riattivazione.</p>
      <H>5. Limitazione di responsabilità</H>
      <p>Il servizio è fornito "così com'è". Il Titolare non è responsabile per interruzioni dei servizi di terzi (Meta, OpenAI, Stripe) né per un uso improprio da parte dell'utente.</p>
      <H>6. Legge applicabile</H>
      <p>I presenti Termini sono regolati dalla legge italiana. Per ogni controversia è competente il foro del luogo in cui ha sede il Titolare.</p>
    </LegalShell>
  );
}

export function Cookies() {
  return (
    <LegalShell icon={Cookie} title="Cookie Policy">
      <p>{FALLBACK.platform_name} utilizza esclusivamente cookie e tecnologie strettamente necessari al funzionamento del servizio.</p>
      <H>Cookie tecnici</H>
      <p>Utilizziamo cookie e archiviazione locale essenziali per l'autenticazione (sessione), la sicurezza e le preferenze (ad es. la lingua). Questi cookie non richiedono consenso in quanto necessari all'erogazione del servizio.</p>
      <H>Cookie di terze parti</H>
      <p>Le funzionalità di pagamento (Stripe) e l'eventuale collegamento a Meta/WhatsApp possono impostare cookie tecnici dei rispettivi provider durante tali operazioni. Non utilizziamo cookie di profilazione pubblicitaria.</p>
      <H>Gestione</H>
      <p>È possibile gestire o eliminare i cookie dalle impostazioni del proprio browser; la disabilitazione dei cookie tecnici può impedire l'accesso al servizio.</p>
    </LegalShell>
  );
}

export function Contact() {
  const p = usePlatform();
  const addr = `${p.platform_address}, ${p.platform_postal_code} ${p.platform_city} (${p.platform_province}), ${p.platform_country}`;
  return (
    <LegalShell icon={Mail} title="Contatti">
      <p>Per qualsiasi richiesta relativa al servizio {p.platform_name}, all'assistenza o al trattamento dei dati personali è possibile contattare il fornitore ai seguenti recapiti.</p>
      <H>Fornitore del servizio</H>
      <p>
        <b>{p.platform_legal_business_name}</b><br />
        {addr}<br />
        P.IVA: {p.platform_vat_number}<br />
        REA: {p.platform_rea}
      </p>
      <H>Email certificata (PEC)</H>
      <p><a className="text-sky-700" href={`mailto:${p.platform_pec}`}>{p.platform_pec}</a></p>
      <p className="text-sm text-slate-500">Per le richieste in materia di privacy (GDPR) utilizzare la PEC sopra indicata specificando l'oggetto della richiesta.</p>
    </LegalShell>
  );
}
